import { useMemo } from 'react';
import { calculateDeck } from '../../../domain/deck/calculateDeck';
import { createDeckPreset, DECK_PRESETS } from '../../../domain/deck/deckPresets';
import type { DeckAttachWall, DeckConfig, SaunaDimensions } from '../../../engines/types';
import type { Market } from '../../../domain/location/types';
import { formatPrice, useT } from '../../../i18n';
import './DeckEditor.css';

interface DeckEditorProps {
  deckConfig: DeckConfig | null;
  saunaDimensions: SaunaDimensions;
  onDeckConfigChange: (config: DeckConfig | null) => void;
  market?: Market;
}

function clampToTenth(value: number, min: number, max: number) {
  return Math.round(Math.max(min, Math.min(max, value)) * 10) / 10;
}

export function DeckEditor({
  deckConfig,
  saunaDimensions,
  onDeckConfigChange,
  market = 'se',
}: DeckEditorProps) {
  const t = useT();

  const getShapeLabel = (shape: DeckConfig['shape']) => {
    switch (shape) {
      case 'l-shape':
        return t.deck_shape_l_shape;
      case 'wrap-front':
        return t.deck_shape_wrap_front;
      case 'wrap-sides':
        return t.deck_shape_wrap_sides;
      case 'rectangular':
      default:
        return t.deck_shape_rectangular;
    }
  };

  const preview = useMemo(
    () => (deckConfig ? calculateDeck(deckConfig, saunaDimensions) : null),
    [deckConfig, saunaDimensions],
  );

  const handleEnableChange = (enabled: boolean) => {
    onDeckConfigChange(enabled ? createDeckPreset('rectangular', saunaDimensions) : null);
  };

  const updateConfig = (patch: Partial<DeckConfig>) => {
    if (!deckConfig) return;
    onDeckConfigChange({ ...deckConfig, ...patch });
  };

  const handleShapeChange = (shape: DeckConfig['shape']) => {
    if (!deckConfig) return;
    onDeckConfigChange(createDeckPreset(shape, saunaDimensions, deckConfig.attachWall));
  };

  const handleAttachWallChange = (attachWall: DeckAttachWall) => {
    if (!deckConfig) return;
    updateConfig({ attachWall });
  };

  const wallSpan = deckConfig?.attachWall === 'left' || deckConfig?.attachWall === 'right'
    ? saunaDimensions.length
    : saunaDimensions.width;
  const maxWidth = Math.max(2, wallSpan + 2);

  return (
    <div className="inspector-section-body">
      <div className="deck-editor">
        <div className="deck-editor__header">
          <div>
            <p className="section-label">{t.deck_sectionLabel}</p>
            <p className="deck-editor__copy">{t.deck_intro}</p>
          </div>
          <label className="deck-editor__toggle">
            <input
              type="checkbox"
              checked={!!deckConfig}
              onChange={(event) => handleEnableChange(event.target.checked)}
            />
            <span>{t.deck_enable}</span>
          </label>
        </div>

        {!deckConfig && (
          <p className="text-muted inspector-empty-state">{t.deck_disabledState}</p>
        )}

        {deckConfig && (
          <>
            <div className="deck-editor__shape-grid" role="radiogroup" aria-label={t.deck_shape}>
              {Object.entries(DECK_PRESETS).map(([shape, preset]) => {
                const active = deckConfig.shape === shape;
                return (
                  <button
                    key={shape}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`deck-editor__shape-card${active ? ' active' : ''}`}
                    onClick={() => handleShapeChange(shape as DeckConfig['shape'])}
                  >
                    <span className="deck-editor__shape-icon" aria-hidden="true">{preset.icon}</span>
                    <span>{getShapeLabel(shape as DeckConfig['shape'])}</span>
                  </button>
                );
              })}
            </div>

            <div className="opening-grid deck-editor__fields">
              <label>
                {t.deck_attachWall}
                <select
                  value={deckConfig.attachWall}
                  onChange={(event) => handleAttachWallChange(event.target.value as DeckAttachWall)}
                >
                  <option value="front">{t.openings_wallFront}</option>
                  <option value="back">{t.openings_wallBack}</option>
                  <option value="left">{t.openings_wallLeft}</option>
                  <option value="right">{t.openings_wallRight}</option>
                </select>
              </label>
              <label>
                {t.deck_boardType}
                <select
                  value={deckConfig.boardType}
                  onChange={(event) => updateConfig({ boardType: event.target.value as DeckConfig['boardType'] })}
                >
                  <option value="28x120">28×120 mm</option>
                  <option value="22x95">22×95 mm</option>
                </select>
              </label>
              <label>
                {t.deck_joistDimension}
                <select
                  value={deckConfig.joistDimension}
                  onChange={(event) => updateConfig({ joistDimension: event.target.value as DeckConfig['joistDimension'] })}
                >
                  <option value="45x145">45×145 mm</option>
                  <option value="45x170">45×170 mm</option>
                </select>
              </label>
            </div>

            <div className="builder-controls">
              <div className="slider-row">
                <div className="slider-label-row">
                  <span className="slider-label">{t.deck_depth}</span>
                  <span className="slider-value">{deckConfig.depth.toFixed(1)} m</span>
                </div>
                <input
                  type="range"
                  className="slider-track"
                  min={1}
                  max={4}
                  step={0.1}
                  value={deckConfig.depth}
                  onChange={(event) => updateConfig({ depth: clampToTenth(Number(event.target.value), 1, 4) })}
                />
              </div>

              <div className="slider-row">
                <div className="slider-label-row">
                  <span className="slider-label">{t.deck_width}</span>
                  <span className="slider-value">{deckConfig.width.toFixed(1)} m</span>
                </div>
                <input
                  type="range"
                  className="slider-track"
                  min={1}
                  max={maxWidth}
                  step={0.1}
                  value={deckConfig.width}
                  onChange={(event) => updateConfig({ width: clampToTenth(Number(event.target.value), 1, maxWidth) })}
                />
              </div>

              {deckConfig.shape === 'l-shape' && (
                <>
                  <div className="slider-row">
                    <div className="slider-label-row">
                      <span className="slider-label">{t.deck_armDepth}</span>
                      <span className="slider-value">{(deckConfig.armDepth ?? 1.5).toFixed(1)} m</span>
                    </div>
                    <input
                      type="range"
                      className="slider-track"
                      min={0.8}
                      max={3}
                      step={0.1}
                      value={deckConfig.armDepth ?? 1.5}
                      onChange={(event) => updateConfig({ armDepth: clampToTenth(Number(event.target.value), 0.8, 3) })}
                    />
                  </div>
                  <div className="slider-row">
                    <div className="slider-label-row">
                      <span className="slider-label">{t.deck_armWidth}</span>
                      <span className="slider-value">{(deckConfig.armWidth ?? 1.5).toFixed(1)} m</span>
                    </div>
                    <input
                      type="range"
                      className="slider-track"
                      min={0.8}
                      max={3}
                      step={0.1}
                      value={deckConfig.armWidth ?? 1.5}
                      onChange={(event) => updateConfig({ armWidth: clampToTenth(Number(event.target.value), 0.8, 3) })}
                    />
                  </div>
                </>
              )}

              {(deckConfig.shape === 'wrap-front' || deckConfig.shape === 'wrap-sides') && (
                <div className="slider-row">
                  <div className="slider-label-row">
                    <span className="slider-label">{t.deck_wrapDepth}</span>
                    <span className="slider-value">{(deckConfig.wrapDepth ?? 1).toFixed(1)} m</span>
                  </div>
                  <input
                    type="range"
                    className="slider-track"
                    min={0.6}
                    max={2.5}
                    step={0.1}
                    value={deckConfig.wrapDepth ?? 1}
                    onChange={(event) => updateConfig({ wrapDepth: clampToTenth(Number(event.target.value), 0.6, 2.5) })}
                  />
                </div>
              )}
            </div>

            {preview && (
              <div className="deck-editor__summary">
                <div className="deck-editor__summary-grid">
                  <div>
                    <span>{t.deck_boards}</span>
                    <strong>{preview.boards.count} st × {(preview.boards.lengthMm / 1000).toFixed(1)} m</strong>
                  </div>
                  <div>
                    <span>{t.deck_joists}</span>
                    <strong>{preview.joists.count} st × {(preview.joists.lengthMm / 1000).toFixed(1)} m</strong>
                  </div>
                  <div>
                    <span>{t.deck_bearers}</span>
                    <strong>{preview.bearers.count} st × {(preview.bearers.lengthMm / 1000).toFixed(1)} m</strong>
                  </div>
                  <div>
                    <span>{t.deck_piers}</span>
                    <strong>{preview.piers.count} st</strong>
                  </div>
                  <div>
                    <span>{t.deck_screws}</span>
                    <strong>{preview.screws.count} st</strong>
                  </div>
                  <div>
                    <span>{t.deck_area}</span>
                    <strong>{preview.areaSqm.toFixed(1)} m²</strong>
                  </div>
                </div>
                <p className="deck-editor__cost">
                  {t.deck_estimatedCost}: <strong>{formatPrice(preview.estimatedCostSek, market)}</strong>
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
